import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal } from "lucide-react";

export default function Category({ mode }) {
  const { slug, name } = useParams();
  const [sp] = useSearchParams();
  const q = sp.get("q");
  const [products, setProducts] = useState([]);
  const [sort, setSort] = useState("popular");
  const [priceMax, setPriceMax] = useState(5000);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = new URLSearchParams({ sort, limit: 200 });
    if (mode === "category") p.set("category", slug);
    if (mode === "festival") p.set("festival", name);
    if (mode === "deity") p.set("deity", name);
    if (mode === "best") p.set("best_seller", "true");
    if (mode === "search" && q) p.set("q", q);
    return p.toString();
  }, [slug, name, sort, mode, q]);

  useEffect(() => {
    setLoading(true);
    api.get(`/products?${params}`).then(({data}) => setProducts(data)).finally(()=>setLoading(false));
  }, [params]);

  const filtered = products.filter(p => p.price <= priceMax);
  const title = mode==="search" ? `Search: "${q}"`
    : mode==="best" ? "Best Sellers"
    : mode==="festival" ? `${name} Collection`
    : mode==="deity" ? `${name} Collection`
    : (slug || "").replace(/-/g," ").replace(/\b\w/g, c=>c.toUpperCase());

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="section-label">{mode || "Category"}</div>
      <h1 className="font-serif text-3xl md:text-5xl mt-2 capitalize">{title}</h1>
      <div className="mt-2 text-sm text-muted-foreground">{filtered.length} products</div>

      <div className="mt-8 grid md:grid-cols-[240px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-6">
          <div className="rounded-xl border border-border p-5 bg-white">
            <div className="flex items-center gap-2 mb-4"><SlidersHorizontal className="w-4 h-4"/><span className="font-medium">Filters</span></div>
            <div>
              <label className="text-xs text-muted-foreground">Sort by</label>
              <select data-testid="sort-select" value={sort} onChange={e=>setSort(e.target.value)} className="pk-input mt-1">
                <option value="popular">Popular</option>
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="rating">Best Rated</option>
              </select>
            </div>
            <div className="mt-5">
              <label className="text-xs text-muted-foreground">Max Price: ₹{priceMax}</label>
              <input data-testid="price-slider" type="range" min={100} max={5000} step={100} value={priceMax} onChange={e=>setPriceMax(+e.target.value)} className="w-full mt-2 accent-primary"/>
            </div>
          </div>
        </aside>

        <div>
          {loading ? <div className="py-16 text-center text-muted-foreground">Loading...</div> :
           filtered.length === 0 ? <div className="py-16 text-center text-muted-foreground">No products found.</div> :
           <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
             {filtered.map(p => <ProductCard key={p.id} p={p}/>)}
           </div>
          }
        </div>
      </div>
    </div>
  );
}
